import { useEffect, useState } from 'react';

import { Tick02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { countries } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { operatorTypes, typesWithLogic } from 'constants/logic';
import { useLocale } from 'hooks/locale';
import { Controller, useWatch } from 'react-hook-form';

import { DatePicker, InputGroup, Select } from 'components/FormComponents';

export const LogicSettings = ({
  index,
  register,
  control,
  fields,
  blockBefore,
  locale = 'en_GB',
}) => {
  const fieldName = `logic_jumps.${index}`;

  const fieldValue = useWatch({ control, name: `${fieldName}.field` });
  const operatorValue = useWatch({ control, name: `${fieldName}.operator` });
  const value = useWatch({ control, name: `${fieldName}.value` });
  const fromValue = useWatch({ control, name: `${fieldName}.from` });
  const toValue = useWatch({ control, name: `${fieldName}.to` });

  const [fieldOptions, setFieldOptions] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [fromOptions, setFromOptions] = useState([]);
  const [toFieldOptions, setToFieldOptions] = useState([]);

  const { dateHyphenFormat } = useLocale(locale);

  // Field options
  useEffect(() => {
    setFieldOptions(
      fields.filter(
        field =>
          typesWithLogic.includes(field.type) &&
          field.order > blockBefore &&
          field.order < fields.length,
      ),
    );
  }, [fields]);

  // Operator / From options
  useEffect(() => {
    const field = fields.find(field => field.order === fieldValue);
    if (!field) return;

    setOperatorOptions(
      operatorTypes.filter(operatorType => operatorType.allowedTypes.includes(field.type)),
    );
    setFromOptions(fields.filter(field => field.order >= fieldValue));
  }, [fieldValue]);

  // To options: fields after fromValue + "Finish form" (-1)
  useEffect(() => {
    if (!fromValue) return;

    const questionOptions = fields.filter(field => field.order > fromValue);
    const finishFormOption = { order: -1, text: 'Finish form', isFinishForm: true };
    setToFieldOptions([...questionOptions, finishFormOption]);
  }, [fromValue, fields]);

  const renderValueField = () => {
    const field = fields.find(field => field.order === fieldValue);
    if (!field) return;

    let start, end, scale;

    switch (field.type) {
      case FieldTypes.MultipleChoice:
        return (
          <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
            {field.options?.options.map(option => (
              <Select.Item key={option.value} value={option.value}>
                {option.value}
              </Select.Item>
            ))}
          </Select>
        );
      case FieldTypes.Date:
        return (
          <Controller
            control={control}
            name={`${fieldName}.value`}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                value={value}
                onChange={onChange}
                format={dateHyphenFormat}
                outputFormat="YYYY-MM-DD"
              />
            )}
          />
        );
      case FieldTypes.YesNo:
        return (
          <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
            <Select.Item value="Yes">Yes</Select.Item>
            <Select.Item value="No">No</Select.Item>
          </Select>
        );
      case FieldTypes.OpinionScale:
        start =
          field.options?.use_custom_steps && field.options.steps_start
            ? parseInt(field.options.steps_start)
            : 1;
        end =
          field.options?.use_custom_steps && field.options.steps_end
            ? parseInt(field.options.steps_end)
            : 10;
        scale = Array(end - start + 1)
          .fill()
          .map((_, index) => start + index);

        return (
          <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
            {scale.map(number => (
              <Select.Item key={number} value={number}>
                {number}
              </Select.Item>
            ))}
          </Select>
        );
      case FieldTypes.StarRating:
        end = field.options?.use_custom_stars ? parseInt(field.options?.stars) : 5;
        scale = Array(end - 2)
          .fill()
          .map((_, index) => 1 + index);

        return (
          <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
            {scale.map(number => (
              <Select.Item key={number} value={number}>
                {number}
              </Select.Item>
            ))}
          </Select>
        );
      case FieldTypes.Country:
        return (
          <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
            {countries.map(country => (
              <Select.Item key={country.value} value={country.value}>
                {country.name}
              </Select.Item>
            ))}
          </Select>
        );
    }
  };

  return (
    <>
      <div className="bg-neutral-1 border border-neutral-4 rounded-2xl p-4 flex flex-col gap-4">
        <div className="flex items-center gap-4 w-full flex-wrap">
          <span className="text-neutral-6 text-xl whitespace-nowrap">When answer for</span>

          <Select name={`${fieldName}.field`} register={register} value={fieldValue} useDefault>
            {fieldOptions.map(field => (
              <Select.Item key={field.order} value={field.order}>
                <div className="flex-1 flex items-center gap-1.5">
                  <div className="w-10 h-10 rounded-full bg-primary-1 flex items-center justify-center shrink-0">
                    <span className="text-neutral-7 text-lg font-medium leading-none">
                      {field.order}
                    </span>
                  </div>{' '}
                  {field.text}
                </div>
              </Select.Item>
            ))}
          </Select>
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <span className="text-neutral-6 text-xl whitespace-nowrap shrink-0">is</span>
            <Select
              name={`${fieldName}.operator`}
              register={register}
              value={operatorValue}
              useDefault
              className="flex-1 min-w-0">
              {operatorOptions.map(operatorOption => (
                <Select.Item key={operatorOption.value} value={operatorOption.value}>
                  {operatorOption.label}
                </Select.Item>
              ))}
            </Select>

            <div className="flex-1 min-w-0">{renderValueField()}</div>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-neutral-6 text-xl whitespace-nowrap">then jump from</span>
          <Select name={`${fieldName}.from`} register={register} value={fromValue} useDefault>
            {fromOptions.map(field => (
              <Select.Item key={field.order} value={field.order}>
                <div className="flex-1 flex items-center gap-1.5">
                  <div className="w-10 h-10 rounded-full bg-primary-1 flex items-center justify-center shrink-0">
                    <span className="text-neutral-7 text-lg font-medium leading-none">
                      {field.order}
                    </span>
                  </div>{' '}
                  {field.text}
                </div>
              </Select.Item>
            ))}
          </Select>
          <span className="text-neutral-6 text-xl whitespace-nowrap">to</span>
          <Select name={`${fieldName}.to`} register={register} value={toValue} useDefault>
            {toFieldOptions.map(field => (
              <Select.Item key={field.order} value={field.order}>
                <div className="flex-1 flex items-center gap-1.5">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      field.isFinishForm ? 'bg-green-2' : 'bg-neutral-6'
                    }`}>
                    {field.isFinishForm ? (
                      <HugeiconsIcon icon={Tick02Icon} size="20" className="text-neutral-1" />
                    ) : (
                      <span className="text-neutral-1 text-lg font-regular leading-none">
                        {field.order}
                      </span>
                    )}
                  </div>{' '}
                  {field.isFinishForm ? (
                    field.text
                  ) : (
                    <span dangerouslySetInnerHTML={{ __html: field.text }} />
                  )}
                </div>
              </Select.Item>
            ))}
          </Select>
        </div>
      </div>
    </>
  );
};
